/**
 * Public error taxonomy for holito's AI authoring tools: conversation and
 * single-question generation, image captions, objective suggestions and
 * brand research.
 *
 * The worker classifies a failure at the authoritative boundary and sends a
 * stable code plus `retryable`. The browser resolves copy and recovery from
 * the code alone. Copy is never derived from raw error text, so provider
 * names, model ids, env var names and stack text cannot reach a payload or
 * the page. Dependency-neutral: imported by the offline-session worker and
 * by browser code.
 *
 * @module lib/generation-errors
 */

/**
 * AI-stage failure classes shared by every surface:
 * - `unavailable`: a dependency outage, rate limit or timeout (after any
 *   provider fallback). Another attempt can succeed.
 * - `unusable_output`: the model answered, but the output was rejected.
 *   Generation is stochastic, so another attempt can succeed.
 * - `failed`: an unexpected defect.
 */
export type GenerationFailureCode = 'unavailable' | 'unusable_output' | 'failed';

/** Source-link import outcomes in the conversation wizard. */
export type SourceImportErrorCode =
  | 'source_access'
  | 'source_typeform_access'
  | 'source_google_incomplete'
  | 'source_microsoft_incomplete'
  | 'source_microsoft_settings'
  | 'source_microsoft_options'
  | 'source_too_large'
  | 'source_timeout'
  | 'source_ambiguous'
  | 'source_intent_unclear'
  | 'source_needs_current_builder'
  | 'source_clone_in_wizard'
  | 'source_incomplete';

/** Brand research outcomes about the website itself. */
export type SiteErrorCode = 'site_invalid' | 'site_unreadable';

/** Every public code a form-generation SSE error event may carry. */
export type FormGenerationErrorCode = GenerationFailureCode | SourceImportErrorCode;

/** Every public code a brand-research failure may carry. */
export type ResearchErrorCode = GenerationFailureCode | SiteErrorCode;

/** The closed set of public generation error codes. */
export type GenerationErrorCode = GenerationFailureCode | SourceImportErrorCode | SiteErrorCode;

/** The authoring surface a failure is presented on. */
export type GenerationSurface = 'form' | 'question' | 'caption' | 'objectives' | 'research';

/** Fixed copy for each source import outcome. Same text on every surface. */
const SOURCE_IMPORT_COPY: Record<SourceImportErrorCode, string> = {
  source_access: 'Could not read the complete source. Use a public Google Forms, Microsoft Forms or Typeform responder link that opens without signing in.',
  source_typeform_access: 'Could not read the complete Typeform source. Use a public responder link that opens without signing in.',
  source_google_incomplete: 'Could not read the complete Google source. Use a public responder link.',
  source_microsoft_incomplete: 'Could not read the complete Microsoft source. Use a public responder link.',
  source_microsoft_settings: 'Could not read the complete Microsoft source settings.',
  source_microsoft_options: 'Could not read all Microsoft answer options.',
  source_too_large: 'The source is too large to import completely. Split it into smaller conversations.',
  source_timeout: 'Reading the source timed out. Try the public responder link again.',
  source_ambiguous: 'Choose one source link to copy or adapt.',
  source_intent_unclear: 'Could not determine how to use the source link. Try again.',
  source_needs_current_builder: 'Open the current conversation builder to import this source with its original answer formats.',
  source_clone_in_wizard: 'Paste the source link in the conversation wizard to copy the entire source.',
  source_incomplete: 'The import did not finish. Try again.',
};

/** Fixed copy for the website conditions brand research can report. */
const SITE_COPY: Record<SiteErrorCode, string> = {
  site_invalid: 'Check the website address.',
  site_unreadable: "Couldn't read this website.",
};

/**
 * Per-surface copy for the AI-stage failure classes. Copy never invites
 * another attempt itself: {@link RETRY_PROMPT_SURFACES} append the prompt
 * only when retry is truthful.
 */
const FAILURE_COPY: Record<GenerationSurface, Record<GenerationFailureCode, string>> = {
  form: {
    unavailable: 'Generation is unavailable right now.',
    unusable_output: 'No usable questions came back.',
    failed: 'Could not generate the conversation.',
  },
  question: {
    unavailable: 'Generation is unavailable right now.',
    unusable_output: 'No usable block came back.',
    failed: 'Couldn’t add this block.',
  },
  caption: {
    unavailable: 'Captions are unavailable right now.',
    unusable_output: 'No usable caption came back.',
    failed: 'Could not write a caption.',
  },
  objectives: {
    unavailable: 'Suggestions are unavailable right now.',
    unusable_output: 'No usable objectives came back.',
    failed: 'Could not suggest objectives.',
  },
  research: {
    unavailable: 'Research is unavailable right now.',
    unusable_output: "Couldn't re-research.",
    failed: "Couldn't re-research.",
  },
};

/**
 * Surfaces with no separate retry control, where the copy itself invites
 * another attempt when (and only when) retry is truthful.
 */
const RETRY_PROMPT_SURFACES: ReadonlySet<GenerationSurface> = new Set(['question']);

/** Codes where another attempt with the same input can change the outcome. */
const RETRYABLE = new Set<GenerationErrorCode>([
  'unavailable',
  'unusable_output',
  'failed',
  'source_timeout',
  'source_intent_unclear',
  'source_incomplete',
]);

/** Every public generation error code, for exhaustive contract tests. */
export const GENERATION_ERROR_CODES: readonly GenerationErrorCode[] = [
  'unavailable',
  'unusable_output',
  'failed',
  ...(Object.keys(SOURCE_IMPORT_COPY) as SourceImportErrorCode[]),
  ...(Object.keys(SITE_COPY) as SiteErrorCode[]),
];

const CODE_SET: ReadonlySet<string> = new Set(GENERATION_ERROR_CODES);

/**
 * Type guard for a public code read from an untrusted payload.
 *
 * @param value - Anything read from the wire
 * @returns True only for a member of the closed code set
 */
export function isGenerationErrorCode(value: unknown): value is GenerationErrorCode {
  return typeof value === 'string' && CODE_SET.has(value);
}

/**
 * Type guard for a code on the `/forms/generate` transport.
 *
 * @param value - Anything read from the wire
 * @returns True only for an AI-stage or source import code
 */
export function isFormGenerationErrorCode(value: unknown): value is FormGenerationErrorCode {
  return isGenerationErrorCode(value) && !Object.prototype.hasOwnProperty.call(SITE_COPY, value);
}

/**
 * Type guard for a code on the brand-research transport.
 *
 * @param value - Anything read from the wire
 * @returns True only for an AI-stage or website code
 */
export function isResearchErrorCode(value: unknown): value is ResearchErrorCode {
  return isGenerationErrorCode(value) && !Object.prototype.hasOwnProperty.call(SOURCE_IMPORT_COPY, value);
}

/**
 * Whether another attempt with the same input can change the outcome.
 *
 * @param code - A public generation error code
 * @returns True when a retry control is truthful
 */
export function isRetryableGenerationError(code: GenerationErrorCode): boolean {
  return RETRYABLE.has(code);
}

/**
 * Combine the code table with a boundary's own retry decision. The boundary
 * can withhold retry (for example after a provider rejected the request) but
 * never grant it for a code the table does not retry.
 *
 * @param code - A public generation error code
 * @param retryable - The boundary's decision, when it made one
 * @returns Whether a retry control is truthful
 */
function effectiveRetryable(code: GenerationErrorCode, retryable: boolean | undefined): boolean {
  return isRetryableGenerationError(code) && retryable !== false;
}

/**
 * Whether an HTTP failure status can change on another attempt: a timeout,
 * a rate limit or a server error. Any other 4xx rejects the request itself.
 *
 * @param status - HTTP status of a failed response
 * @returns True for 408, 429 and 5xx
 */
export function isRetryableHttpStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

/**
 * Resolve the copy shown for a code on a surface. The only source of
 * user-visible generation error text.
 *
 * @param surface - Where the failure is presented
 * @param code - A public generation error code
 * @param retryable - The failure's retry decision; defaults to the code table.
 *   When false, the copy never invites another attempt.
 * @returns One short sentence (two when it names the next step)
 */
export function resolveGenerationErrorMessage(
  surface: GenerationSurface,
  code: GenerationErrorCode,
  retryable?: boolean,
): string {
  if (Object.prototype.hasOwnProperty.call(SOURCE_IMPORT_COPY, code)) return SOURCE_IMPORT_COPY[code as SourceImportErrorCode];
  if (Object.prototype.hasOwnProperty.call(SITE_COPY, code)) return SITE_COPY[code as SiteErrorCode];
  const copy = FAILURE_COPY[surface][code as GenerationFailureCode];
  return RETRY_PROMPT_SURFACES.has(surface) && effectiveRetryable(code, retryable) ? `${copy} Try again.` : copy;
}

/** JSON error body for a generation endpoint. */
export interface GenerationErrorBody {
  /** Safe copy resolved from the code, for clients that only read `error`. */
  error: string;
  /** Stable public code clients branch on. */
  error_code: GenerationErrorCode;
  /** Whether a retry control is truthful. */
  retryable: boolean;
}

/**
 * Build the JSON body for a failed generation request from its code and the
 * boundary's retry decision.
 *
 * @param surface - The surface the endpoint serves
 * @param code - The classified public code
 * @param retryable - The boundary's retry decision; can only withhold retry
 * @returns The allowlisted body
 */
export function generationErrorBody(
  surface: GenerationSurface,
  code: GenerationErrorCode,
  retryable?: boolean,
): GenerationErrorBody {
  const canRetry = effectiveRetryable(code, retryable);
  return {
    error: resolveGenerationErrorMessage(surface, code, canRetry),
    error_code: code,
    retryable: canRetry,
  };
}

/** SSE `error` event for `/forms/generate`. */
export interface FormGenerationErrorEvent {
  type: 'error';
  /** Stable public code clients branch on. */
  code: FormGenerationErrorCode;
  /** Whether a retry control is truthful. */
  retryable: boolean;
  /** Safe copy resolved from the code, for clients that predate `code`. */
  message: string;
}

/**
 * Build the SSE error event for a failed form or question generation.
 *
 * @param surface - `form` for the wizard, `question` for single-question generation
 * @param code - The classified public code
 * @param retryable - The boundary's retry decision; can only withhold retry
 * @returns The allowlisted event
 */
export function formGenerationErrorEvent(
  surface: 'form' | 'question',
  code: FormGenerationErrorCode,
  retryable?: boolean,
): FormGenerationErrorEvent {
  const canRetry = effectiveRetryable(code, retryable);
  return {
    type: 'error',
    code,
    retryable: canRetry,
    message: resolveGenerationErrorMessage(surface, code, canRetry),
  };
}

/**
 * A source-link import outcome. Its message is the fixed copy for its code,
 * so it is safe to log and to show, and the handler classifies it by class.
 */
export class SourceImportError extends Error {
  /** The public code for this outcome. */
  readonly code: SourceImportErrorCode;

  /** @param code - The public code for this outcome */
  constructor(code: SourceImportErrorCode) {
    super(SOURCE_IMPORT_COPY[code]);
    this.name = 'SourceImportError';
    this.code = code;
  }
}

/** Options for {@link GenerationRequestError}. */
export interface GenerationRequestErrorOptions {
  /** The server's retry decision; can only withhold retry. */
  retryable?: boolean;
  /**
   * Whether the server classified (and captured) the failure. Defaults to
   * true; false means the client detected it and should capture it.
   */
  serverReported?: boolean;
}

/**
 * Browser-side carrier of a public code from the transport to the UI, so a
 * thrown failure keeps its code and retry decision instead of a free-text
 * message.
 */
export class GenerationRequestError extends Error {
  /** The public code the UI resolves copy and recovery from. */
  readonly code: GenerationErrorCode;
  /** Whether a retry control is truthful. */
  readonly retryable: boolean;
  /** Whether the server classified and captured the failure. */
  readonly serverReported: boolean;

  /**
   * @param code - The public code
   * @param options - Optional retry decision and reporting origin
   */
  constructor(code: GenerationErrorCode, options: GenerationRequestErrorOptions = {}) {
    super(code);
    this.name = 'GenerationRequestError';
    this.code = code;
    this.retryable = effectiveRetryable(code, options.retryable);
    this.serverReported = options.serverReported ?? true;
  }
}
